import React, { useState } from 'react';
import { HiOutlineUserPlus, HiOutlineTrash, HiOutlineUserGroup } from 'react-icons/hi2';

const Step3_TeamFormation = ({ incidentData, readOnly = false }) => {
  const [team, setTeam] = useState(incidentData.investigationTeam || []);
  const [newUser, setNewUser] = useState('');
  const [newRole, setNewRole] = useState('Investigator');

  // Hardcoded list of valid users specifically for the mock UI
  const DUMMY_USERS = [
    { id: 101, name: "Jane Smith" },
    { id: 102, name: "Mike Johnson" },
    { id: 103, name: "Sarah Lee" },
    { id: 104, name: "Tom Clark" },
    { id: 105, name: "David Wilson" },
    { id: 106, name: "Chris Davis" },
    { id: 107, name: "Alex Turner" }
  ];

  if (!incidentData) return null;

  const handleAddMember = () => {
    if (!newUser) return;

    // Check if already in team
    if (team.some(m => m.userId.toString() === newUser)) return;

    const userObj = DUMMY_USERS.find(u => u.id.toString() === newUser);
    if (userObj) {
      setTeam([...team, { userId: userObj.id, name: userObj.name, role: newRole }]);
      setNewUser('');
      setNewRole('Investigator');
    }
  };

  const handleRemoveMember = (userId) => {
    setTeam(team.filter(m => m.userId !== userId));
  };

  return (
    <div className="bg-card p-6 rounded-xl border border-gray-200 shadow-sm max-w-4xl mx-auto my-0">
      <div className="border-b border-gray-200 pb-4 mb-6 flex items-start justify-between">
        <div>
          <h2 className="text-md font-bold text-primary flex items-center gap-2">
            <HiOutlineUserGroup className="w-6 h-6 text-blue-600" />
            Investigation Team Formation
          </h2>
          <p className="text-tertiary text-sm mt-1">Select 3-4 multi-disciplinary members to conduct the Root Cause Analysis.</p>
        </div>
      </div>

      <div className="space-y-6">

        {/* Add New Member row */}
        {!readOnly && (
          <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-100 flex items-end gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-secondary mb-1">Select User</label>
              <select
                className="w-full rounded-md border-1px border-gray-300 p-2 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-base bg-card"
                value={newUser}
                onChange={(e) => setNewUser(e.target.value)}
              >
                <option value="">-- Choose Employee --</option>
                {DUMMY_USERS.map(user => (
                  <option key={user.id} value={user.id}>{user.name}</option>
                ))}
              </select>
            </div>
            <div className="w-48">
              <label className="block text-base font-medium text-secondary mb-1">Role</label>
              <select
                className="w-full rounded-md text-sm p-2 border-1px border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-base bg-card"
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
              >
                <option value="Lead Investigator">Lead Investigator</option>
                <option value="Investigator">Investigator</option>
                <option value="Safety Officer">Safety Officer</option>
                <option value="Subject Matter Expert">Subject Matter Expert</option>
              </select>
            </div>
            <button
              onClick={handleAddMember}
              disabled={!newUser}
              className="px-4 py-2 text-white font-medium rounded-lg transition-colors flex items-center gap-2 shadow-sm h-[38px] disabled:opacity-50"
              style={{ backgroundColor: '#509183' }}
              onMouseEnter={e => !e.currentTarget.disabled && (e.currentTarget.style.backgroundColor = '#3d7268')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#509183')}
            >
              <HiOutlineUserPlus className="w-5 h-5" />
              Add Member
            </button>
          </div>
        )}

        {/* Current Team List */}
        <div>
          <h3 className="text-base font-semibold text-secondary uppercase tracking-wider mb-3">Current Team ({team.length})</h3>

          {team.length === 0 ? (
            <div className="text-center p-6 border-2 border-dashed border-gray-200 rounded-lg text-tertiary bg-page-bg">
              No team members assigned yet.
            </div>
          ) : (
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-page-bg">
                  <tr>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-base font-semibold text-primary sm:pl-6">Name</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-base font-semibold text-primary">Role assigned</th>
                    {!readOnly && <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6"><span className="sr-only">Remove</span></th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-card">
                  {team.map((member) => (
                    <tr key={member.userId}>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-base font-medium text-primary sm:pl-6 flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-base">
                          {member.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        {member.name}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-base text-tertiary">
                        <span className={`px-2 inline-flex text-base leading-5 font-semibold rounded-full 
                          ${member.role === 'Lead Investigator' ? 'bg-purple-100 text-purple-800' :
                            member.role === 'Safety Officer' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}`}>
                          {member.role}
                        </span>
                      </td>
                      {!readOnly && (
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-base font-medium sm:pr-6">
                          <button
                            onClick={() => handleRemoveMember(member.userId)}
                            className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded"
                          >
                            <HiOutlineTrash className="w-5 h-5" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default Step3_TeamFormation;
